import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Upload, Store, Loader2, AlertCircle, Search, MapPinOff, ImageOff, CalendarOff, TagIcon } from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { ProducerMaturityListItem } from '@/components/admin/ProducerMaturityListItem';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminFabEffect } from '@/hooks/useAdminFabEffect';
import { useBatchCovers } from '@/hooks/useBatchCovers';
import {
  useAdminProducersWithMaturity,
  type ProducerSegment,
  type AdminProducerRow,
} from '@/hooks/useAdminProducersWithMaturity';

type QuickFilter = 'no_place' | 'no_image' | 'no_category' | 'no_activity' | null;

function getQuickFilterCounts(producers: AdminProducerRow[]) {
  let noPlace = 0, noImage = 0, noCategory = 0, noActivity = 0;
  for (const p of producers) {
    if (!p.has_place) noPlace++;
    if (!p.has_cover_image) noImage++;
    if (p.categories.length === 0) noCategory++;
    if (!p.has_activity) noActivity++;
  }
  return { noPlace, noImage, noCategory, noActivity };
}

function applyQuickFilter(producers: AdminProducerRow[], filter: QuickFilter): AdminProducerRow[] {
  if (!filter) return producers;
  switch (filter) {
    case 'no_place': return producers.filter((p) => !p.has_place);
    case 'no_image': return producers.filter((p) => !p.has_cover_image);
    case 'no_category': return producers.filter((p) => p.categories.length === 0);
    case 'no_activity': return producers.filter((p) => !p.has_activity);
  }
}

export default function AdminProducers() {
  const [segment, setSegment] = useState<ProducerSegment>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const fabConfig = useMemo(() => ({
    id: 'admin-producers-create',
    ariaLabel: 'Lägg till värd',
    onClick: () => navigate('/admin/producers/new'),
    visibility: 'mobile' as const,
  }), [navigate]);

  useAdminFabEffect(fabConfig);

  const { data: allProducers = [], isLoading, error } = useAdminProducersWithMaturity();

  const segmentFiltered = useMemo(() => {
    if (segment === 'attention') return allProducers.filter((r) => r.needs_attention);
    if (segment === 'public') return allProducers.filter((r) => r.status === 'public');
    if (segment === 'draft') return allProducers.filter((r) => r.status === 'draft');
    return allProducers;
  }, [allProducers, segment]);

  const searchFiltered = useMemo(() => {
    if (!search) return segmentFiltered;
    const q = search.toLowerCase();
    return segmentFiltered.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.city && r.city.toLowerCase().includes(q)) ||
        (r.region && r.region.toLowerCase().includes(q))
    );
  }, [segmentFiltered, search]);

  const quickCounts = useMemo(() => getQuickFilterCounts(searchFiltered), [searchFiltered]);
  const producers = useMemo(() => applyQuickFilter(searchFiltered, quickFilter), [searchFiltered, quickFilter]);

  const visibleProducerIds = useMemo(() => producers.map((producer) => producer.id), [producers]);
  const { coverMap } = useBatchCovers('producer', visibleProducerIds, 320);

  const toggleQuick = (f: QuickFilter) => setQuickFilter((prev) => (prev === f ? null : f));

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Värdar</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Kurera, granska mognad och hantera gårdar.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" disabled className="hidden sm:inline-flex">
              <Upload className="h-4 w-4 mr-2" />
              Importera
              <Badge variant="secondary" className="ml-2 text-[10px]">
                Snart
              </Badge>
            </Button>
            <Button asChild>
              <Link to="/admin/producers/new">
                <Plus className="h-4 w-4 mr-2" />
                Lägg till värd
              </Link>
            </Button>
          </div>
        </div>

        {/* Segment tabs */}
        <Tabs value={segment} onValueChange={(v) => { setSegment(v as ProducerSegment); setQuickFilter(null); }}>
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="all">Alla</TabsTrigger>
            <TabsTrigger value="attention">Behöver åtgärd</TabsTrigger>
            <TabsTrigger value="public">Publicerade</TabsTrigger>
            <TabsTrigger value="draft">Utkast</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Sök värd eller ort…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Quick filters */}
        {allProducers.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {quickCounts.noPlace > 0 && (
              <QuickFilterChip
                active={quickFilter === 'no_place'}
                onClick={() => toggleQuick('no_place')}
                icon={<MapPinOff className="h-3 w-3" />}
                label="Saknar plats"
                count={quickCounts.noPlace}
              />
            )}
            {quickCounts.noImage > 0 && (
              <QuickFilterChip
                active={quickFilter === 'no_image'}
                onClick={() => toggleQuick('no_image')}
                icon={<ImageOff className="h-3 w-3" />}
                label="Saknar bild"
                count={quickCounts.noImage}
              />
            )}
            {quickCounts.noCategory > 0 && (
              <QuickFilterChip
                active={quickFilter === 'no_category'}
                onClick={() => toggleQuick('no_category')}
                icon={<TagIcon className="h-3 w-3" />}
                label="Saknar kategori"
                count={quickCounts.noCategory}
              />
            )}
            {quickCounts.noActivity > 0 && (
              <QuickFilterChip
                active={quickFilter === 'no_activity'}
                onClick={() => toggleQuick('no_activity')}
                icon={<CalendarOff className="h-3 w-3" />}
                label="Saknar aktivitet"
                count={quickCounts.noActivity}
              />
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error ? error.message : 'Kunde inte hämta värdar'}
            </AlertDescription>
          </Alert>
        )}

        {/* Loading */}
        {isLoading ? (
          <div className="text-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
            <p className="text-muted-foreground mt-4 text-sm">Laddar värdar…</p>
          </div>
        ) : producers.length === 0 ? (
          <div className="text-center py-16">
            <Store className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {search || segment !== 'all' || quickFilter
                ? 'Inga värdar matchar'
                : 'Inga värdar ännu'}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {search || segment !== 'all' || quickFilter
                ? 'Prova att ändra dina filter eller sökning.'
                : 'Kom igång genom att lägga till din första värd.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              {(search || segment !== 'all' || quickFilter) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('');
                    setSearchInput('');
                    setSegment('all');
                    setQuickFilter(null);
                  }}
                >
                  Rensa filter
                </Button>
              )}
              <Button asChild>
                <Link to="/admin/producers/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Lägg till värd
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {producers.map((p) => (
                <ProducerMaturityListItem key={p.id} producer={p} coverUrl320={coverMap.get(p.id) ?? null} />
              ))}
            </div>

            <p className="text-xs text-muted-foreground text-center pt-2">
              Visar {producers.length} värd{producers.length !== 1 ? 'ar' : ''}
              {quickFilter && ` (filtrerat)`}
            </p>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

/* ── Quick filter chip ── */

function QuickFilterChip({
  active,
  onClick,
  icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border bg-card text-muted-foreground hover:bg-muted/50'
      }`}
    >
      {icon}
      {label}
      <span className="tabular-nums font-medium">{count}</span>
    </button>
  );
}
